import * as React from 'react';
import CachedIcon from '@mui/icons-material/Cached';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import UpdateButton from 'shared/components/UpdateButton';
import { Snapshot } from '../types';

export default function SnapshotHeader({
  snapshot,
  onRefresh,
  onSettings,
  loading,
}: {
  snapshot: Snapshot | null;
  onRefresh: () => void;
  onSettings?: () => void;
  loading?: boolean;
}) {
  const generated = snapshot?.generated_at
    ? new Date(snapshot.generated_at)
    : null;
  const label = generated
    ? `${generated.toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })} PT`
    : 'no snapshot yet';
  // Numbers refresh hourly, but the written explanations only update on the
  // 4×/day full runs — surface their age right next to "Last refreshed".
  const narrativeAsOf =
    snapshot?.narrative_as_of &&
    snapshot.narrative_as_of !== snapshot.snapshot_local_label
      ? snapshot.narrative_as_of
      : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
      }}
    >
      <Box>
        <Typography variant="h5">Meta Ads Insights — Dosaze</Typography>
        <Typography variant="body2" color="text.secondary">
          {loading ? 'Refreshing… (pulling fresh data, ~1–2 min)' : `Last refreshed ${label}`}
        </Typography>
        {!loading && narrativeAsOf && (
          <Typography variant="body2" color="text.secondary">
            🕐 Explanations written {narrativeAsOf} — refreshed 4×/day (8am / 12 / 4 / 8pm PT)
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {onSettings && (
            <Tooltip title="Settings — ad-spend cutoff & narrative token budget">
              <IconButton onClick={onSettings} size="small">
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <UpdateButton
            variant="outlined"
            endIcon={<CachedIcon />}
            loading={loading}
            onClick={onRefresh}
          >
            Refresh data
          </UpdateButton>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ maxWidth: 230, textAlign: 'right', lineHeight: 1.3 }}
        >
          Numbers refresh hourly (and on Refresh) · written analysis 4×/day
        </Typography>
      </Box>
    </Box>
  );
}
